import React, { memo, useMemo, useCallback } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import moment from 'moment';
import 'react-dates/initialize';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Button from '@amzn/awsui-components-react/polaris/button';
import DatePicker from '@amzn/awsui-components-react/polaris/date-picker';
import Select from '@amzn/awsui-components-react/polaris/select';
import Tabs from '@amzn/awsui-components-react/polaris/tabs';
import TimeInput from '@amzn/awsui-components-react/polaris/time-input';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __values(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var DATE_FORMAT = 'YYYY-MM-DD';

var Tab;
(function (Tab) {
    Tab["Absolute"] = "ABSOLUTE";
    Tab["Relative"] = "RELATIVE";
})(Tab || (Tab = {}));
var Tab$1 = Tab;

var TimeZone;
(function (TimeZone) {
    TimeZone["Local"] = "LOCAL";
    TimeZone["UTC"] = "UTC";
})(TimeZone || (TimeZone = {}));
var TimeZone$1 = TimeZone;

var InfoErrorMessage;
(function (InfoErrorMessage) {
    InfoErrorMessage["END_DATE_BLANK"] = "End date should not be blank.";
    InfoErrorMessage["END_DATE_OUTSIDE_RANGE"] = "End date is outside the valid range.";
    InfoErrorMessage["END_TIME_BLANK"] = "End time should not be blank.";
    InfoErrorMessage["START_DATE_BLANK"] = "Start date should not be blank.";
    InfoErrorMessage["START_END_DATE_BLANK"] = "Start date and end date should not be blank.";
    InfoErrorMessage["START_DATE_OUTSIDE_RANGE"] = "Start date is outside the valid range.";
    InfoErrorMessage["START_NOT_EARLIER_THAN_END"] = "Start time should be earlier than end time.";
    InfoErrorMessage["START_TIME_BLANK"] = "Start time should not be blank.";
})(InfoErrorMessage || (InfoErrorMessage = {}));
var InfoErrorMessage$1 = InfoErrorMessage;

var MILLISECONDS_PER_SECOND = 1000;
var MILLISECONDS_PER_MINUTE = 60 * MILLISECONDS_PER_SECOND;
var MILLISECONDS_PER_HOUR = 60 * MILLISECONDS_PER_MINUTE;
var MILLISECONDS_PER_DAY = 24 * MILLISECONDS_PER_HOUR;
var MILLISECONDS_PER_WEEK = 7 * MILLISECONDS_PER_DAY;
var MILLISECONDS_PER_MONTH = 30 * MILLISECONDS_PER_DAY;

function batchedUpdates(f) {
    unstable_batchedUpdates(f);
}

function isInteger(n) {
    return n === Math.round(n);
}

function timeFormatToPlaceholder(timeFormat, isStart) {
    if (isStart) {
        switch (timeFormat) {
            case 'HH':
            case 'hh':
                return '00';
            case 'HH:mm':
            case 'hh:mm':
                return '00:00';
            case 'HH:mm:ss':
            case 'hh:mm:ss':
                return '00:00:00';
        }
    }
    else {
        switch (timeFormat) {
            case 'HH':
            case 'hh':
                return '23';
            case 'HH:mm':
            case 'hh:mm':
                return '23:59';
            case 'HH:mm:ss':
            case 'hh:mm:ss':
                return '23:59:59';
        }
    }
}

function convertTimeRangeToNewTimeZone(timeRange, previousTimeZone, newTimeZone) {
    var offsetDirection;
    if (previousTimeZone === newTimeZone) {
        return timeRange;
    }
    if (!Array.isArray(timeRange)) {
        return timeRange;
    }
    if (newTimeZone === TimeZone$1.Local) {
        offsetDirection = 1;
    }
    else {
        offsetDirection = -1;
    }
    var localTimeZoneOffset = new Date().getTimezoneOffset();
    var start = timeRange[0];
    var end = timeRange[1];
    var newStart = start;
    var newEnd = end;
    if (start !== null) {
        newStart = moment(start)
            .add(localTimeZoneOffset * offsetDirection, 'm')
            .valueOf();
    }
    if (newEnd !== null) {
        newEnd = moment(end)
            .add(localTimeZoneOffset * offsetDirection, 'm')
            .valueOf();
    }
    return [newStart, newEnd];
}

var UNITS = [
    MILLISECONDS_PER_MINUTE,
    MILLISECONDS_PER_HOUR,
    MILLISECONDS_PER_DAY,
    MILLISECONDS_PER_WEEK,
    MILLISECONDS_PER_MONTH,
];
function useRelativeTab(_a) {
    var onChange = _a.onChange, phrases = _a.phrases, slowPicks = _a.slowPicks, value = _a.value;
    var defaultMillisecondsPerUnit = UNITS[0];
    if (value) {
        for (var i = UNITS.length - 1; i > 1; i--) {
            var millisecondsPerUnit_1 = UNITS[i];
            if (isInteger(value / millisecondsPerUnit_1)) {
                defaultMillisecondsPerUnit = millisecondsPerUnit_1;
                break;
            }
        }
    }
    var _b = __read(React.useState(defaultMillisecondsPerUnit), 2), millisecondsPerUnit = _b[0], setMillisecondsPerUnit = _b[1];
    var unitOptions = React.useMemo(function () {
        return [
            {
                id: MILLISECONDS_PER_MINUTE.toString(),
                label: phrases.minutes || 'Minutes',
            },
            { id: MILLISECONDS_PER_HOUR.toString(), label: phrases.hours || 'Hours' },
            { id: MILLISECONDS_PER_DAY.toString(), label: phrases.days || 'Days' },
            { id: MILLISECONDS_PER_WEEK.toString(), label: phrases.weeks || 'Weeks' },
            {
                id: MILLISECONDS_PER_MONTH.toString(),
                label: phrases.months || 'Months',
            },
        ];
    }, [
        phrases.days,
        phrases.hours,
        phrases.minutes,
        phrases.weeks,
        phrases.months,
    ]);
    var inputValue = value
        ? (value / millisecondsPerUnit).toString()
        : '';
    var handleInputValueInput = React.useCallback(function (e) {
        if (e.detail.value === '') {
            onChange(null);
            return;
        }
        var intValue = parseInt(e.detail.value, 10);
        if (!Number.isNaN(intValue)) {
            onChange(intValue * millisecondsPerUnit);
        }
    }, [millisecondsPerUnit, onChange]);
    var handleUnitChange = React.useCallback(function (e) {
        var newMillisecondsPerUnit = parseInt(e.detail.selectedOption.id, 10);
        setMillisecondsPerUnit(newMillisecondsPerUnit);
        if (inputValue) {
            onChange(parseInt(inputValue, 10) * newMillisecondsPerUnit);
        }
    }, [inputValue, onChange]);
    var _c = __read(React.useMemo(function () {
        var e_1, _a;
        var splitSlowPicks = [[], [], [], [], []];
        var _loop_1 = function (slowPick) {
            var _loop_2 = function (i) {
                var millisecondsPerUnit_2 = UNITS[i];
                var units = slowPick / millisecondsPerUnit_2;
                if (isInteger(units)) {
                    var classNames = [
                        'awsui-date-time-range__relative-time__cell',
                    ];
                    if (value === slowPick) {
                        classNames.push('awsui-date-time-range__relative-time__cell--selected');
                    }
                    splitSlowPicks[i].push(React.createElement("td", { className: classNames.join(' '), key: slowPick },
                        React.createElement("a", { "data-testid": "date-time-range-relative-option-".concat(slowPick), href: "#", onClick: function (e) {
                                e.preventDefault();
                                setMillisecondsPerUnit(millisecondsPerUnit_2);
                                onChange(slowPick);
                            } }, units)));
                    return "break";
                }
            };
            for (var i = UNITS.length - 1; i >= 0; i--) {
                var state_1 = _loop_2(i);
                if (state_1 === "break")
                    break;
            }
        };
        try {
            for (var slowPicks_1 = __values(slowPicks), slowPicks_1_1 = slowPicks_1.next(); !slowPicks_1_1.done; slowPicks_1_1 = slowPicks_1.next()) {
                var slowPick = slowPicks_1_1.value;
                _loop_1(slowPick);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (slowPicks_1_1 && !slowPicks_1_1.done && (_a = slowPicks_1.return)) _a.call(slowPicks_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return splitSlowPicks;
    }, [onChange, slowPicks, value]), 5), slowPickMinutes = _c[0], slowPickHours = _c[1], slowPickDays = _c[2], slowPickWeeks = _c[3], slowPickMonths = _c[4];
    return {
        daysPhrase: phrases.days || 'Days',
        footerColSpan: Math.max(slowPickMinutes.length, slowPickHours.length, slowPickDays.length, slowPickWeeks.length, slowPickMonths.length) + 1,
        handleInputValueInput: handleInputValueInput,
        handleUnitChange: handleUnitChange,
        hoursPhrase: phrases.hours || 'Hours',
        inputValue: inputValue,
        minutesPhrase: phrases.minutes || 'Minutes',
        monthsPhrase: phrases.months || 'Months',
        selectedUnitOption: unitOptions.find(function (unitOption) {
            return unitOption.id === millisecondsPerUnit.toString();
        }) || unitOptions[0],
        slowPickDays: slowPickDays,
        slowPickHours: slowPickHours,
        slowPickMinutes: slowPickMinutes,
        slowPickWeeks: slowPickWeeks,
        slowPickMonths: slowPickMonths,
        unitOptions: unitOptions,
        weeksPhrase: phrases.weeks || 'Weeks',
    };
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$5 = ".awsui .awsui-date-time-range__relative-time{color:#16191f;color:var(--awsui-color-text-body-default,#16191f);min-width:unset}.awsui .awsui-date-time-range__relative-time__footer{align-items:center;display:flex;flex-direction:row;justify-content:center;padding:1em .5em 0}.awsui .awsui-date-time-range__relative-time__footer>*{padding:0 .5em}.awsui .awsui-date-time-range__relative-time__footer .awsui-input{text-align:right}.awsui .awsui-date-time-range__relative-time>tbody>tr>th{font-size:.8em;padding:0 2em;text-align:left}.awsui .awsui-date-time-range__relative-time .awsui-date-time-range__relative-time__cell{padding:.5em;width:3rem}.awsui .awsui-date-time-range__relative-time .awsui-date-time-range__relative-time__cell>a{align-items:center;border:1px solid #eaeded;border-color:var(--awsui-color-border-divider-default,#eaeded);border-radius:2px;color:#16191f;color:var(--awsui-color-text-body-default,#16191f);display:inline-flex;font-size:.8em;height:3rem;justify-content:center;text-decoration:none;width:5rem}.awsui .awsui-date-time-range__relative-time .awsui-date-time-range__relative-time__cell--selected>a{background-color:#f1faff;background-color:var(--awsui-color-background-item-selected,#f1faff);border-color:#00a1c9;border-color:var(--awsui-color-border-item-selected,#00a1c9)}";
styleInject(css_248z$5);

var DEFAULT_SLOW_PICKS = [
    5 * MILLISECONDS_PER_MINUTE,
    10 * MILLISECONDS_PER_MINUTE,
    15 * MILLISECONDS_PER_MINUTE,
    30 * MILLISECONDS_PER_MINUTE,
    45 * MILLISECONDS_PER_MINUTE,
    1 * MILLISECONDS_PER_HOUR,
    2 * MILLISECONDS_PER_HOUR,
    3 * MILLISECONDS_PER_HOUR,
    6 * MILLISECONDS_PER_HOUR,
    8 * MILLISECONDS_PER_HOUR,
    12 * MILLISECONDS_PER_HOUR,
    1 * MILLISECONDS_PER_DAY,
    2 * MILLISECONDS_PER_DAY,
    3 * MILLISECONDS_PER_DAY,
    4 * MILLISECONDS_PER_DAY,
    5 * MILLISECONDS_PER_DAY,
    6 * MILLISECONDS_PER_DAY,
    1 * MILLISECONDS_PER_WEEK,
    2 * MILLISECONDS_PER_WEEK,
    3 * MILLISECONDS_PER_WEEK,
    4 * MILLISECONDS_PER_WEEK,
];
function RelativeTab(_a) {
    var onChange = _a.onChange, phrases = _a.phrases, _b = _a.slowPicks, slowPicks = _b === void 0 ? DEFAULT_SLOW_PICKS : _b, value = _a.value;
    var _c = useRelativeTab({
        onChange: onChange,
        phrases: phrases,
        slowPicks: slowPicks,
        value: value,
    }), daysPhrase = _c.daysPhrase, footerColSpan = _c.footerColSpan, handleInputValueInput = _c.handleInputValueInput, handleUnitChange = _c.handleUnitChange, hoursPhrase = _c.hoursPhrase, inputValue = _c.inputValue, minutesPhrase = _c.minutesPhrase, monthsPhrase = _c.monthsPhrase, selectedUnitOption = _c.selectedUnitOption, slowPickDays = _c.slowPickDays, slowPickHours = _c.slowPickHours, slowPickMinutes = _c.slowPickMinutes, slowPickMonths = _c.slowPickMonths, slowPickWeeks = _c.slowPickWeeks, unitOptions = _c.unitOptions, weeksPhrase = _c.weeksPhrase;
    return (React.createElement("table", { className: "awsui-date-time-range__relative-time" },
        React.createElement("tbody", null,
            slowPickMinutes.length > 0 && (React.createElement("tr", { "aria-label": minutesPhrase },
                React.createElement("th", null, minutesPhrase),
                slowPickMinutes)),
            slowPickHours.length > 0 && (React.createElement("tr", { "aria-label": hoursPhrase },
                React.createElement("th", null, hoursPhrase),
                slowPickHours)),
            slowPickDays.length > 0 && (React.createElement("tr", { "aria-label": daysPhrase },
                React.createElement("th", null, daysPhrase),
                slowPickDays)),
            slowPickWeeks.length > 0 && (React.createElement("tr", { "aria-label": weeksPhrase },
                React.createElement("th", null, weeksPhrase),
                slowPickWeeks)),
            slowPickMonths.length > 0 && (React.createElement("tr", { "aria-label": monthsPhrase },
                React.createElement("th", null, monthsPhrase),
                slowPickMonths))),
        React.createElement("tfoot", null,
            React.createElement("tr", null,
                React.createElement("td", { colSpan: footerColSpan },
                    React.createElement("div", { className: "awsui-date-time-range__relative-time__footer" },
                        React.createElement(Input, { autocomplete: false, autofocus: true, onChange: handleInputValueInput, onInput: handleInputValueInput, type: "number", value: inputValue }),
                        React.createElement(Select, { onChange: handleUnitChange, options: unitOptions, selectedOption: selectedUnitOption })))))));
}

function useInfo(_a) {
    var onEndDateChange = _a.onEndDateChange, onEndTimeChange = _a.onEndTimeChange, onRelativeTimeChange = _a.onRelativeTimeChange, onStartDateChange = _a.onStartDateChange, onStartTimeChange = _a.onStartTimeChange, onTabChange = _a.onTabChange, onTimeZoneChange = _a.onTimeZoneChange, phrases = _a.phrases, relativeTime = _a.relativeTime, slowPicks = _a.slowPicks, timeZone = _a.timeZone;
    var handleEndDateChange = React.useCallback(function (e) {
        onEndDateChange(e.detail.value);
    }, [onEndDateChange]);
    var handleEndTimeChange = React.useCallback(function (e) {
        onEndTimeChange(e.detail.value);
    }, [onEndTimeChange]);
    var handleStartDateChange = React.useCallback(function (e) {
        onStartDateChange(e.detail.value);
    }, [onStartDateChange]);
    var handleStartTimeChange = React.useCallback(function (e) {
        onStartTimeChange(e.detail.value);
    }, [onStartTimeChange]);
    var handleTabChange = React.useCallback(function (e) {
        onTabChange(e.detail.activeTabId);
    }, [onTabChange]);
    var handleTimeZoneChange = React.useCallback(function (e) {
        if (onTimeZoneChange) {
            onTimeZoneChange(e.detail.selectedOption.id);
        }
    }, [onTimeZoneChange]);
    var tabs = React.useMemo(function () {
        return [
            {
                content: null,
                id: Tab$1.Absolute,
                label: phrases.absolute || 'Absolute',
            },
            {
                content: function () {
                    return (React.createElement(RelativeTab, { onChange: onRelativeTimeChange, phrases: {
                            days: phrases.days,
                            hours: phrases.hours,
                            minutes: phrases.minutes,
                            months: phrases.months,
                            weeks: phrases.weeks,
                        }, slowPicks: slowPicks, value: relativeTime }));
                },
                id: Tab$1.Relative,
                label: phrases.relative || 'Relative',
            },
        ];
    }, [
        onRelativeTimeChange,
        phrases.absolute,
        phrases.days,
        phrases.hours,
        phrases.minutes,
        phrases.months,
        phrases.relative,
        phrases.weeks,
        relativeTime,
        slowPicks,
    ]);
    var timeZoneOptions = React.useMemo(function () {
        return [
            { id: TimeZone$1.Local, label: phrases.localTimeZone || 'Local' },
            { id: TimeZone$1.UTC, label: 'UTC' },
        ];
    }, [phrases.localTimeZone]);
    var selectedTimeZoneOption = timeZoneOptions.find(function (timeZoneOption) {
        return timeZoneOption.id === timeZone;
    }) || timeZoneOptions[0];
    return {
        handleEndDateChange: handleEndDateChange,
        handleEndTimeChange: handleEndTimeChange,
        handleStartDateChange: handleStartDateChange,
        handleStartTimeChange: handleStartTimeChange,
        handleTabChange: handleTabChange,
        handleTimeZoneChange: handleTimeZoneChange,
        selectedTimeZoneOption: selectedTimeZoneOption,
        tabs: tabs,
        timeZoneOptions: timeZoneOptions,
    };
}

var css_248z$4 = ".awsui .awsui-date-time-range.awsui-date-time-range--absolute .awsui-date-time-range__header{left:0;position:absolute;top:-1px;width:100%}.awsui .awsui-date-time-range.awsui-date-time-range--absolute .awsui-date-time-range__header__time-zone{top:0}.awsui .awsui-date-time-range.awsui-date-time-range--relative .awsui-date-time-range__header__time-zone{top:-1px}.awsui .awsui-date-time-range__error-message{padding:0 1em 1em}.awsui .awsui-date-time-range__header__time-zone{padding:.5em 1em;position:absolute;right:0}.awsui .awsui-date-time-range__header .awsui-tabs-tab-link{padding:.5rem 0}.awsui .awsui-date-time-range__info{align-items:stretch;display:flex;flex-direction:column;justify-content:stretch}.awsui .awsui-date-time-range__info__buttons{align-items:center;border-top:1px solid #eaeded;border-top-color:var(--awsui-color-border-container-top,#eaeded);display:flex;flex-direction:row;justify-content:flex-end;padding:.5em 1em;text-align:right}.awsui .awsui-date-time-range__info__buttons__spacer{flex-grow:1}.awsui .awsui-date-time-range__info__help-text{color:#545b64;color:var(--awsui-color-text-body-secondary,#545b64);padding:0 1.5em 1em}.awsui .awsui-date-time-range__info__inputs{align-items:center;display:flex;flex-direction:row;justify-content:center;padding:0 1em 1em}.awsui .awsui-date-time-range__info__inputs>awsui-form-field{flex-grow:1;padding:0 .5em}.awsui .awsui-date-time-range__info__inputs .awsui-input-icon{display:none!important}.awsui .awsui-date-time-range__info__inputs .awsui-input-dropdown__dropdown{display:none}";
styleInject(css_248z$4);

function Info(_a) {
    var absoluteHelpText = _a.absoluteHelpText, endDate = _a.endDate, endTime = _a.endTime, error = _a.error, hideClear = _a.hideClear, onApply = _a.onApply, onCancel = _a.onCancel, onClear = _a.onClear, onEndDateChange = _a.onEndDateChange, onEndTimeChange = _a.onEndTimeChange, onErrorMessageDismiss = _a.onErrorMessageDismiss, onRelativeTimeChange = _a.onRelativeTimeChange, onStartDateChange = _a.onStartDateChange, onStartTimeChange = _a.onStartTimeChange, onTabChange = _a.onTabChange, onTimeZoneChange = _a.onTimeZoneChange, phrases = _a.phrases, relativeTime = _a.relativeTime, slowPicks = _a.slowPicks, startDate = _a.startDate, startTime = _a.startTime, tab = _a.tab, timeFormat = _a.timeFormat, timeZone = _a.timeZone, displayTimeZoneOutside = _a.displayTimeZoneOutside;
    var _b = useInfo({
        onEndDateChange: onEndDateChange,
        onEndTimeChange: onEndTimeChange,
        onRelativeTimeChange: onRelativeTimeChange,
        onStartDateChange: onStartDateChange,
        onStartTimeChange: onStartTimeChange,
        onTabChange: onTabChange,
        onTimeZoneChange: onTimeZoneChange,
        phrases: phrases,
        relativeTime: relativeTime,
        slowPicks: slowPicks,
        timeZone: timeZone,
    }), handleEndDateChange = _b.handleEndDateChange, handleEndTimeChange = _b.handleEndTimeChange, handleStartDateChange = _b.handleStartDateChange, handleStartTimeChange = _b.handleStartTimeChange, handleTabChange = _b.handleTabChange, handleTimeZoneChange = _b.handleTimeZoneChange, selectedTimeZoneOption = _b.selectedTimeZoneOption, tabs = _b.tabs, timeZoneOptions = _b.timeZoneOptions;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "awsui-date-time-range__header" },
            React.createElement("div", null,
                React.createElement(Tabs, { activeTabId: tab, onChange: handleTabChange, tabs: tabs })),
            displayTimeZoneOutside ? null : (React.createElement("div", { className: "awsui-date-time-range__header__time-zone" },
                React.createElement(Select, { onChange: handleTimeZoneChange, options: timeZoneOptions, selectedOption: selectedTimeZoneOption })))),
        React.createElement("div", { className: "awsui-date-time-range__info" },
            tab === Tab$1.Absolute && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "awsui-date-time-range__info__inputs" },
                    React.createElement(FormField, { label: phrases.startDateAndTime, stretch: true },
                        React.createElement("div", { className: "awsui-row" },
                            React.createElement(DatePicker, { ariaRequired: true, autofocus: false, className: "col-7", label: phrases.startDate, onChange: handleStartDateChange, placeholder: "YYYY/MM/DD", value: startDate }),
                            React.createElement(TimeInput, { ariaRequired: true, autocomplete: false, autofocus: false, className: "col-5", "data-testid": "Start time", format: timeFormat, label: phrases.startTime, onChange: handleStartTimeChange, placeholder: timeFormatToPlaceholder(timeFormat, true), use24Hour: true, value: startTime }))),
                    React.createElement(FormField, { label: phrases.endDateAndTime, stretch: true },
                        React.createElement("div", { className: "awsui-row" },
                            React.createElement(DatePicker, { ariaRequired: true, autofocus: false, className: "col-7", label: phrases.endDate, onChange: handleEndDateChange, placeholder: "YYYY/MM/DD", value: endDate }),
                            React.createElement(TimeInput, { ariaRequired: true, autocomplete: false, autofocus: false, className: "col-5", "data-testid": "End time", format: timeFormat, label: phrases.endTime, onChange: handleEndTimeChange, placeholder: timeFormatToPlaceholder(timeFormat, false), use24Hour: true, value: endTime })))),
                absoluteHelpText && (React.createElement("div", { className: "awsui-date-time-range__info__help-text" }, absoluteHelpText)))),
            error && (React.createElement(Alert, { className: "awsui-date-time-range__error-message", dismissible: true, onDismiss: onErrorMessageDismiss, type: "error" }, error.message)),
            React.createElement("div", { className: "awsui-date-time-range__info__buttons" },
                !hideClear && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { onClick: onClear, variant: "normal" }, phrases.clear || 'Clear'),
                    React.createElement("span", { className: "awsui-date-time-range__info__buttons__spacer" }))),
                React.createElement(Button, { onClick: onCancel, variant: "link" }, phrases.cancel || 'Cancel'),
                React.createElement(Button, { onClick: onApply, variant: "primary" }, phrases.apply || 'Apply')))));
}

var css_248z$3 = ".awsui .awsui-date-time-range__nav{height:30px;position:absolute;text-align:center;top:20px;width:40px}.awsui .awsui-date-time-range__nav.awsui-date-time-range__nav--next{right:22px}.awsui .awsui-date-time-range__nav.awsui-date-time-range__nav--prev{left:22px}";
styleInject(css_248z$3);

function Nav(_a) {
    var variant = _a.variant;
    return (React.createElement("div", { className: "awsui-date-time-range__nav awsui-date-time-range__nav--".concat(variant) },
        React.createElement(Icon, { name: variant === 'next' ? 'angle-right' : 'angle-left' })));
}

function useQuickPicks(_a) {
    var phrases = _a.phrases, quickPicks = _a.quickPicks, relativeTime = _a.relativeTime, selected = _a.selected;
    var humanReadable = React.useCallback(function (milliseconds) {
        if (milliseconds === null) {
            return phrases.clear || 'Clear';
        }
        var months = milliseconds / MILLISECONDS_PER_MONTH;
        if (isInteger(months)) {
            return "".concat(months).concat(phrases.monthsAbbr || 'M');
        }
        var weeks = milliseconds / MILLISECONDS_PER_WEEK;
        if (isInteger(weeks)) {
            return "".concat(weeks).concat(phrases.weeksAbbr || 'w');
        }
        var days = milliseconds / MILLISECONDS_PER_DAY;
        if (isInteger(days)) {
            return "".concat(days).concat(phrases.daysAbbr || 'd');
        }
        var hours = milliseconds / MILLISECONDS_PER_HOUR;
        if (isInteger(hours)) {
            return "".concat(hours).concat(phrases.hoursAbbr || 'h');
        }
        var minutes = milliseconds / MILLISECONDS_PER_MINUTE;
        if (isInteger(minutes)) {
            return "".concat(minutes).concat(phrases.minutesAbbr || 'm');
        }
        return "".concat(Math.round(milliseconds / 1000)).concat(phrases.secondsAbbr || 's');
    }, [
        phrases.clear,
        phrases.daysAbbr,
        phrases.hoursAbbr,
        phrases.minutesAbbr,
        phrases.monthsAbbr,
        phrases.secondsAbbr,
        phrases.weeksAbbr,
    ]);
    var isQuickPick = quickPicks.findIndex(function (quickPick) { return quickPick === relativeTime; }) !== -1;
    var customClassNames = [
        'awsui-date-time-range__quick-picks__custom',
    ];
    if (!isQuickPick && relativeTime !== null && selected) {
        customClassNames.push('awsui-date-time-range__quick-picks--selected');
    }
    return {
        customClassName: customClassNames.join(' '),
        customText: "".concat(phrases.custom || 'Custom').concat(relativeTime && !isQuickPick && selected
            ? " (".concat(humanReadable(relativeTime), ") ")
            : ''),
        humanReadable: humanReadable,
    };
}

var css_248z$2 = ".awsui .awsui-date-time-range__quick-picks{align-items:center;background-color:#fff;background-color:var(--awsui-color-background-container-content,#fff);border:1px solid #aab7b8;border-color:var(--awsui-color-border-control-default,#aab7b8);box-sizing:border-box;display:flex;flex-direction:row;justify-content:space-around;width:397px}.awsui .awsui-date-time-range__quick-picks>.awsui-date-time-range__quick-picks--selected .awsui-button{color:#0073bb;color:var(--awsui-color-text-link-default,#0073bb);font-weight:700}.awsui .awsui-date-time-range__quick-picks>.awsui-date-time-range__quick-picks__custom{border:0 solid #eaeded;border-color:var(--awsui-color-border-divider-default,#eaeded);border-left:1px solid var(--awsui-color-border-divider-default,#eaeded)}.awsui .awsui-date-time-range__quick-picks>.awsui-date-time-range__quick-picks__custom .awsui-button{white-space:nowrap}.awsui .awsui-date-time-range__quick-picks .awsui-button{font-weight:400;height:28px;padding-left:1rem;padding-right:1rem}";
styleInject(css_248z$2);

function QuickPicks(_a) {
    var onExpand = _a.onExpand, onPick = _a.onPick, phrases = _a.phrases, quickPicks = _a.quickPicks, relativeTime = _a.relativeTime, selected = _a.selected, hideCustom = _a.hideCustom;
    var _b = useQuickPicks({
        phrases: phrases,
        quickPicks: quickPicks,
        relativeTime: relativeTime,
        selected: selected,
    }), customClassName = _b.customClassName, customText = _b.customText, humanReadable = _b.humanReadable;
    return (React.createElement("div", { className: "awsui-date-time-range__quick-picks" },
        quickPicks.map(function (quickPick) {
            var classNames = [];
            if (quickPick === relativeTime && quickPick !== null && selected) {
                classNames.push('awsui-date-time-range__quick-picks--selected');
            }
            var key = quickPick === null ? '' : quickPick;
            return (React.createElement("div", { className: classNames.join(' '), key: key },
                React.createElement(Button, { onClick: function () {
                        onPick(quickPick);
                    }, variant: "link" }, humanReadable(quickPick))));
        }),
        !hideCustom && (React.createElement("div", { className: customClassName },
            React.createElement(Button, { icon: "calendar", iconAlign: "right", onClick: onExpand, variant: "link" }, customText)))));
}

var css_248z$1 = ".awsui .awsui-time-zone-picker{margin-left:calc(-1rem - 1px);padding:.5rem}";
styleInject(css_248z$1);

function TimeZonePicker(_a) {
    var _b = _a.selectedTimeZone, selectedTimeZone = _b === void 0 ? TimeZone$1.Local : _b, onChange = _a.onChange, phrases = _a.phrases;
    var timeZoneOptions = useMemo(function () {
        return [
            {
                id: TimeZone$1.Local,
                label: phrases.localTimeZone || 'Local',
            },
            {
                id: TimeZone$1.UTC,
                label: 'UTC',
            },
        ];
    }, [phrases.localTimeZone]);
    var selectedTimeZoneOption = useMemo(function () {
        return timeZoneOptions.find(function (timeZoneOption) { return timeZoneOption.id === selectedTimeZone; }) || timeZoneOptions[0];
    }, [timeZoneOptions, selectedTimeZone]);
    var handleTimeZoneChange = useCallback(function (_a) {
        var detail = _a.detail;
        onChange(detail.selectedOption.id);
    }, [onChange]);
    return (React.createElement(Select, { className: "awsui-time-zone-picker", onChange: handleTimeZoneChange, options: timeZoneOptions, selectedOption: selectedTimeZoneOption, ariaDescribedby: "Time Zone", "data-testid": "Time-zone-picker" }));
}
var TimeZonePicker$1 = memo(TimeZonePicker);

var css_248z = ".awsui-date-time-range-container{display:flex;flex-direction:row}.awsui .awsui-date-time-range{display:inline-block;font-family:Amazon Ember,Helvetica Neue,Roboto,Arial,sans-serif;padding:.5rem;z-index:11}.awsui .awsui-date-time-range .CalendarDay__blocked_out_of_range,.awsui .awsui-date-time-range .CalendarDay__blocked_out_of_range:active,.awsui .awsui-date-time-range .CalendarDay__blocked_out_of_range:hover{color:#879596;color:var(--awsui-color-text-input-disabled,#879596)}.awsui .awsui-date-time-range .CalendarDay__default{background-color:#fff;background-color:var(--awsui-color-background-input-default,#fff);outline-width:0}.awsui .awsui-date-time-range .CalendarDay__default:not(.CalendarDay__blocked_out_of_range){color:#16191f;color:var(--awsui-color-text-form-default,#16191f)}.awsui .awsui-date-time-range .CalendarDay__default:not(.CalendarDay__blocked_out_of_range):hover{background-color:#f2f3f3;background-color:var(--awsui-color-background-dropdown-item-hover,#f2f3f3);border-color:#879596;border-color:var(--awsui-color-border-dropdown-item-hover,#879596);border-style:double}.awsui .awsui-date-time-range .CalendarDay__hovered_span,.awsui .awsui-date-time-range .CalendarDay__hovered_span:hover{background-color:#f2f3f3;background-color:var(--awsui-color-background-dropdown-item-hover,#f2f3f3);border-color:#879596;border-color:var(--awsui-color-border-dropdown-item-hover,#879596);color:#16191f;color:var(--awsui-color-text-form-default,#16191f)}.awsui .awsui-date-time-range .CalendarDay__selected,.awsui .awsui-date-time-range .CalendarDay__selected:active,.awsui .awsui-date-time-range .CalendarDay__selected:hover{border-color:#00a1c9;border-color:var(--awsui-color-border-item-selected,#00a1c9)}.awsui .awsui-date-time-range .CalendarDay__selected,.awsui .awsui-date-time-range .CalendarDay__selected:active,.awsui .awsui-date-time-range .CalendarDay__selected:hover,.awsui .awsui-date-time-range .CalendarDay__selected_span{background-color:#f1faff;background-color:var(--awsui-color-background-item-selected,#f1faff);color:#16191f;color:var(--awsui-color-text-form-default,#16191f)}.awsui .awsui-date-time-range .CalendarDay__selected_span{border-style:double solid solid double;border-width:1px}.awsui .awsui-date-time-range .DateRangePickerInput{background-color:#fff;background-color:var(--awsui-color-background-input-default,#fff)}.awsui .awsui-date-time-range .DateRangePickerInput__withBorder{border-style:solid;border-width:1px}.awsui .awsui-date-time-range .CalendarMonth,.awsui .awsui-date-time-range .CalendarMonthGrid,.awsui .awsui-date-time-range .DayPicker,.awsui .awsui-date-time-range .DayPicker__horizontal{background-color:#fff;background-color:var(--awsui-color-background-container-content,#fff)}.awsui .awsui-date-time-range .CalendarMonth_caption{color:#16191f;color:var(--awsui-color-text-heading-default,#16191f)}.awsui .awsui-date-time-range .DateInput{width:170px}.awsui .awsui-date-time-range .DateInput_fang{display:none}.awsui .awsui-date-time-range .DateInput_input{background-color:#fff;background-color:var(--awsui-color-background-control-default,#fff);border-width:0;box-sizing:border-box;color:#16191f;color:var(--awsui-color-text-form-default,#16191f);font-family:Amazon Ember,Helvetica Neue,Roboto,Arial,sans-serif;font-size:1.4rem;font-weight:400;height:28px;line-height:2rem;padding:.4rem 1rem}.awsui .awsui-date-time-range .DateRangePicker_picker{background-color:transparent;left:-205px!important}.awsui .awsui-date-time-range .DateRangePickerInput__withBorder{border-color:#aab7b8;border-color:var(--awsui-color-border-control-default,#aab7b8)}.awsui .awsui-date-time-range .DateRangePickerInput_calendarIcon{padding:4px}.awsui .awsui-date-time-range .DayPicker{left:-17px}.awsui .awsui-date-time-range .DayPicker_weekHeader{margin-top:-1rem}.awsui .awsui-date-time-range--absolute>.awsui-date-time-range__quick-picks{display:none}.awsui .awsui-date-time-range--absolute .DateRangePickerInput__withBorder{align-items:center;display:flex;flex-direction:row}.awsui .awsui-date-time-range--absolute .DayPicker{padding-top:3em;top:-35px}.awsui .awsui-date-time-range--relative .DateInput,.awsui .awsui-date-time-range--relative .awsui-date-time-range__nav{display:none}.awsui .awsui-date-time-range--relative .DateRangePickerInput__withBorder{border-width:0}.awsui .awsui-date-time-range--relative .DateRangePickerInput_arrow,.awsui .awsui-date-time-range--relative .DateRangePickerInput_calendarIcon{display:none}.awsui .awsui-date-time-range--relative .DayPicker{top:-66px}.awsui .awsui-date-time-range--relative .DayPicker>div>div:first-child{position:absolute;visibility:hidden}.awsui .awsui-date-time-range small{color:unset;display:unset;font-size:80%;line-height:unset}.awsui .awsui-date-time-range ul{margin:1px 0;padding-left:0}.awsui awsui-table .awsui-date-time-range table tbody tr td{border-color:#eaeded;border-color:var(--awsui-color-border-divider-default,#eaeded);border-width:0;padding:unset}.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child{border-left-color:#eaeded;border-left-color:var(--awsui-color-border-divider-default,#eaeded);height:auto;padding-left:unset}.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child.CalendarDay__default:not(.CalendarDay__blocked_out_of_range):hover,.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child.CalendarDay__hovered_span,.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child.CalendarDay__hovered_span:hover{border-left-color:#879596;border-left-color:var(--awsui-color-border-dropdown-item-hover,#879596)}.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child.CalendarDay__selected,.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child.CalendarDay__selected:active,.awsui awsui-table .awsui-date-time-range table tbody tr td:first-child.CalendarDay__selected:hover{border-left-color:#00a1c9;border-left-color:var(--awsui-color-border-item-selected,#00a1c9)}.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child{border-right-color:#eaeded;border-right-color:var(--awsui-color-border-divider-default,#eaeded);padding-right:unset}.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child.CalendarDay__default:not(.CalendarDay__blocked_out_of_range):hover,.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child.CalendarDay__hovered_span,.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child.CalendarDay__hovered_span:hover{border-right-color:#879596;border-right-color:var(--awsui-color-border-dropdown-item-hover,#879596)}.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child.CalendarDay__selected,.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child.CalendarDay__selected:active,.awsui awsui-table .awsui-date-time-range table tbody tr td:last-child.CalendarDay__selected:hover{border-right-color:#00a1c9;border-right-color:var(--awsui-color-border-item-selected,#00a1c9)}.awsui awsui-table .awsui-date-time-range table tbody tr td.awsui-date-time-range__relative-time__cell{padding:.5em}.awsui awsui-table .awsui-date-time-range table tbody tr td.CalendarDay__default{border-width:1px}";
styleInject(css_248z);

function useUpdateLayoutEffect(effect, deps) {
    if (deps === void 0) { deps = []; }
    var firstRun = React.useRef(true);
    React.useLayoutEffect(function () {
        if (firstRun.current === true) {
            firstRun.current = false;
            return;
        }
        return effect();
    }, deps);
}

function getInitialRelativeRange(value) {
    if (typeof value === 'number') {
        return value;
    }
    return null;
}

function hasEnd(value) {
    return Array.isArray(value) && typeof value[1] === 'number';
}

function hasStart(value) {
    return Array.isArray(value) && typeof value[0] === 'number';
}

function setTime(moment, time) {
    var dateTime = moment.clone();
    if (typeof time === 'string') {
        var _a = __read(time.split(':'), 3), hours = _a[0], minutes = _a[1], seconds = _a[2];
        dateTime.hours(parseInt(hours, 10));
        dateTime.minutes(parseInt(minutes, 10));
        dateTime.seconds(parseInt(seconds, 10));
        return dateTime;
    }
    dateTime.hours(time.hours());
    dateTime.minutes(time.minutes());
    dateTime.seconds(time.seconds());
    dateTime.milliseconds(time.milliseconds());
    return dateTime;
}

var momentFromMs = function (ms, timeZone) {
    var newMoment = moment.unix(ms / 1000);
    switch (timeZone) {
        case TimeZone$1.Local:
            return newMoment.local();
        case TimeZone$1.UTC:
            return newMoment.utc();
    }
};
function useAbsoluteRange(_a) {
    var defaultEndTime = _a.defaultEndTime, defaultStartTime = _a.defaultStartTime, initialTimeZone = _a.initialTimeZone, timeFormat = _a.timeFormat, value = _a.value;
    var endValue = useMemo(function () {
        if (hasEnd(value)) {
            return value[1];
        }
        return null;
    }, [value]);
    var startValue = useMemo(function () {
        if (hasStart(value)) {
            return value[0];
        }
        return null;
    }, [value]);
    var initialEndMoment = React.useMemo(function () {
        if (endValue !== null) {
            return momentFromMs(endValue, initialTimeZone);
        }
        return null;
    }, [initialTimeZone, endValue]);
    var initialEndDate = React.useMemo(function () {
        if (initialEndMoment === null) {
            return '';
        }
        return initialEndMoment.format(DATE_FORMAT);
    }, [initialEndMoment]);
    var initialEndTime = React.useMemo(function () {
        if (initialEndMoment === null) {
            return defaultEndTime || '';
        }
        return initialEndMoment.format(timeFormat);
    }, [defaultEndTime, initialEndMoment, timeFormat]);
    var initialStartMoment = React.useMemo(function () {
        if (startValue !== null) {
            return momentFromMs(startValue, initialTimeZone);
        }
        return null;
    }, [initialTimeZone, startValue]);
    var initialStartDate = React.useMemo(function () {
        if (initialStartMoment === null) {
            return '';
        }
        return initialStartMoment.format(DATE_FORMAT);
    }, [initialStartMoment]);
    var initialStartTime = React.useMemo(function () {
        if (initialStartMoment === null) {
            return defaultStartTime || '';
        }
        return initialStartMoment.format(timeFormat);
    }, [defaultStartTime, initialStartMoment, timeFormat]);
    var _b = __read(React.useState(initialEndDate), 2), endDate = _b[0], setEndDate = _b[1];
    var _c = __read(React.useState(initialEndMoment), 2), endMoment = _c[0], setEndMoment = _c[1];
    var _d = __read(React.useState(initialEndTime), 2), endTime = _d[0], setEndTime = _d[1];
    var _e = __read(React.useState(initialStartDate), 2), startDate = _e[0], setStartDate = _e[1];
    var _f = __read(React.useState(initialStartMoment), 2), startMoment = _f[0], setStartMoment = _f[1];
    var _g = __read(React.useState(initialStartTime), 2), startTime = _g[0], setStartTime = _g[1];
    var handleEndDateChange = React.useCallback(function (newEndDate) {
        batchedUpdates(function () {
            if (newEndDate === '') {
                setEndDate('');
                setEndMoment(null);
                return;
            }
            var newEndMoment = moment(newEndDate, DATE_FORMAT);
            if (endTime === '') {
                var newEndTime = defaultEndTime || timeFormatToPlaceholder(timeFormat, false);
                setEndTime(newEndTime);
                newEndMoment = setTime(newEndMoment, newEndTime);
            }
            else {
                newEndMoment = setTime(newEndMoment, endTime);
            }
            setEndDate(newEndDate);
            setEndMoment(newEndMoment);
        });
    }, [defaultEndTime, endTime, timeFormat]);
    var handleEndMomentChange = React.useCallback(function (newEndMoment) {
        batchedUpdates(function () {
            if (newEndMoment === null) {
                setEndDate('');
                setEndMoment(null);
                return;
            }
            var renewedEndMoment;
            if (endTime === '') {
                var newEndTime = defaultEndTime || timeFormatToPlaceholder(timeFormat, false);
                setEndTime(newEndTime);
                renewedEndMoment = setTime(newEndMoment, newEndTime);
            }
            else {
                renewedEndMoment = setTime(newEndMoment, endTime);
            }
            setEndDate(renewedEndMoment.format(DATE_FORMAT));
            setEndMoment(renewedEndMoment);
        });
    }, [defaultEndTime, endTime, timeFormat]);
    var handleEndTimeChange = React.useCallback(function (newEndTime) {
        batchedUpdates(function () {
            if (newEndTime === '') {
                setEndMoment(null);
                setEndTime('');
                return;
            }
            if (endDate !== '') {
                var newEndMoment = moment(endDate, DATE_FORMAT);
                newEndMoment = setTime(newEndMoment, newEndTime);
                setEndMoment(newEndMoment);
            }
            setEndTime(newEndTime);
        });
    }, [endDate]);
    var handleStartDateChange = React.useCallback(function (newStartDate) {
        batchedUpdates(function () {
            if (newStartDate === '') {
                setStartDate('');
                setStartMoment(null);
                return;
            }
            var newStartMoment = moment(newStartDate, DATE_FORMAT);
            if (startTime === '') {
                var newStartTime = defaultStartTime || timeFormatToPlaceholder(timeFormat, true);
                setStartTime(newStartTime);
                newStartMoment = setTime(newStartMoment, newStartTime);
            }
            else {
                newStartMoment = setTime(newStartMoment, startTime);
            }
            setStartDate(newStartDate);
            setStartMoment(newStartMoment);
        });
    }, [defaultStartTime, startTime, timeFormat]);
    var handleStartMomentChange = React.useCallback(function (newStartMoment) {
        batchedUpdates(function () {
            if (newStartMoment === null) {
                setStartDate('');
                setStartMoment(null);
                return;
            }
            var renewedStartMoment;
            if (startTime === '') {
                var newStartTime = defaultStartTime || timeFormatToPlaceholder(timeFormat, true);
                setStartTime(newStartTime);
                renewedStartMoment = setTime(newStartMoment, newStartTime);
            }
            else {
                renewedStartMoment = setTime(newStartMoment, startTime);
            }
            setStartDate(renewedStartMoment.format(DATE_FORMAT));
            setStartMoment(renewedStartMoment);
        });
    }, [defaultStartTime, startTime, timeFormat]);
    var handleStartTimeChange = React.useCallback(function (newStartTime) {
        batchedUpdates(function () {
            if (newStartTime === '') {
                setStartMoment(null);
                setStartTime('');
                return;
            }
            if (startDate !== '') {
                var newStartMoment = moment(startDate, DATE_FORMAT);
                newStartMoment = setTime(newStartMoment, newStartTime);
                setStartMoment(newStartMoment);
            }
            setStartTime(newStartTime);
        });
    }, [startDate]);
    var resetAbsoluteRange = React.useCallback(function () {
        batchedUpdates(function () {
            setEndDate(initialEndDate);
            setEndMoment(initialEndMoment);
            setEndTime(initialEndTime);
            setStartDate(initialStartDate);
            setStartMoment(initialStartMoment);
            setStartTime(initialStartTime);
        });
    }, [
        initialEndDate,
        initialEndMoment,
        initialEndTime,
        initialStartDate,
        initialStartMoment,
        initialStartTime,
    ]);
    useUpdateLayoutEffect(function () {
        setEndDate(initialEndDate);
    }, [initialEndDate]);
    useUpdateLayoutEffect(function () {
        setEndMoment(initialEndMoment);
    }, [initialEndMoment]);
    useUpdateLayoutEffect(function () {
        setEndTime(initialEndTime);
    }, [initialEndTime]);
    useUpdateLayoutEffect(function () {
        setStartDate(initialStartDate);
    }, [initialStartDate]);
    useUpdateLayoutEffect(function () {
        setStartMoment(initialStartMoment);
    }, [initialStartMoment]);
    useUpdateLayoutEffect(function () {
        setStartTime(initialStartTime);
    }, [initialStartTime]);
    return {
        endDate: endDate,
        endMoment: endMoment,
        endTime: endTime,
        handleEndDateChange: handleEndDateChange,
        handleEndMomentChange: handleEndMomentChange,
        handleEndTimeChange: handleEndTimeChange,
        handleStartDateChange: handleStartDateChange,
        handleStartMomentChange: handleStartMomentChange,
        handleStartTimeChange: handleStartTimeChange,
        resetAbsoluteRange: resetAbsoluteRange,
        setEndDate: setEndDate,
        setEndMoment: setEndMoment,
        startDate: startDate,
        startMoment: startMoment,
        startTime: startTime,
    };
}

var DEFAULT_END_DATE = 'End date';
var DEFAULT_END_DATE_AND_TIME = 'End date and time';
var DEFAULT_END_TIME = 'End time';
var DEFAULT_START_DATE = 'Start date';
var DEFAULT_START_DATE_AND_TIME = 'Start date and time';
var DEFAULT_START_TIME = 'Start time';
function usePhrases(phrases) {
    return React.useMemo(function () {
        if (!phrases) {
            return [
                undefined,
                {
                    endDate: DEFAULT_END_DATE,
                    endDateAndTime: DEFAULT_END_DATE_AND_TIME,
                    endTime: DEFAULT_END_TIME,
                    startDate: DEFAULT_START_DATE,
                    startDateAndTime: DEFAULT_START_DATE_AND_TIME,
                    startTime: DEFAULT_START_TIME,
                },
                Object.create(null),
            ];
        }
        var absolute = phrases.absolute, apply = phrases.apply, cancel = phrases.cancel, clear = phrases.clear, custom = phrases.custom, days = phrases.days, daysAbbr = phrases.daysAbbr, endDate = phrases.endDate, endDateAndTime = phrases.endDateAndTime, endTime = phrases.endTime, hours = phrases.hours, hoursAbbr = phrases.hoursAbbr, localTimeZone = phrases.localTimeZone, minutes = phrases.minutes, minutesAbbr = phrases.minutesAbbr, months = phrases.months, monthsAbbr = phrases.monthsAbbr, relative = phrases.relative, secondsAbbr = phrases.secondsAbbr, startDate = phrases.startDate, startDateAndTime = phrases.startDateAndTime, startTime = phrases.startTime, weeks = phrases.weeks, weeksAbbr = phrases.weeksAbbr, dateRangePickerPhrases = __rest(phrases, ["absolute", "apply", "cancel", "clear", "custom", "days", "daysAbbr", "endDate", "endDateAndTime", "endTime", "hours", "hoursAbbr", "localTimeZone", "minutes", "minutesAbbr", "months", "monthsAbbr", "relative", "secondsAbbr", "startDate", "startDateAndTime", "startTime", "weeks", "weeksAbbr"]);
        return [
            Object.keys(dateRangePickerPhrases).length > 0
                ? dateRangePickerPhrases
                : undefined,
            {
                absolute: absolute,
                apply: apply,
                cancel: cancel,
                clear: clear,
                days: days,
                endDate: endDate || DEFAULT_END_DATE,
                endDateAndTime: endDateAndTime || DEFAULT_END_DATE_AND_TIME,
                endTime: endTime || DEFAULT_END_TIME,
                hours: hours,
                localTimeZone: localTimeZone,
                minutes: minutes,
                months: months,
                relative: relative,
                startDate: startDate || DEFAULT_START_DATE,
                startDateAndTime: startDateAndTime || DEFAULT_START_DATE_AND_TIME,
                startTime: startTime || DEFAULT_START_TIME,
                weeks: weeks,
            },
            {
                clear: clear,
                custom: custom,
                daysAbbr: daysAbbr,
                hoursAbbr: hoursAbbr,
                minutesAbbr: minutesAbbr,
                monthsAbbr: monthsAbbr,
                secondsAbbr: secondsAbbr,
                weeksAbbr: weeksAbbr,
            },
        ];
    }, [phrases]);
}

function useRelativeRange(value) {
    var initialRelativeRange = getInitialRelativeRange(value);
    var _a = __read(React.useState(initialRelativeRange), 2), relativeRange = _a[0], setRelativeRange = _a[1];
    var resetRelativeRange = React.useCallback(function () {
        if (relativeRange !== initialRelativeRange) {
            setRelativeRange(initialRelativeRange);
        }
    }, [initialRelativeRange, relativeRange]);
    useUpdateLayoutEffect(function () {
        setRelativeRange(initialRelativeRange);
    }, [initialRelativeRange]);
    return [relativeRange, setRelativeRange, resetRelativeRange];
}

function useTabs(value) {
    var initialTab = Array.isArray(value) ? Tab$1.Absolute : Tab$1.Relative;
    var _a = __read(React.useState(initialTab), 2), tab = _a[0], setTab = _a[1];
    var resetTab = React.useCallback(function () {
        if (tab !== initialTab) {
            setTab(initialTab);
        }
    }, [initialTab, tab]);
    useUpdateLayoutEffect(function () {
        setTab(initialTab);
    }, [initialTab]);
    return [tab, setTab, resetTab];
}

function useTimeZone(initialTimeZone) {
    var _a = __read(React.useState(initialTimeZone), 2), timeZone = _a[0], setTimeZone = _a[1];
    var resetTimeZone = React.useCallback(function () {
        if (timeZone !== initialTimeZone) {
            setTimeZone(initialTimeZone);
        }
    }, [initialTimeZone, timeZone]);
    useUpdateLayoutEffect(function () {
        setTimeZone(initialTimeZone);
    }, [initialTimeZone]);
    return [timeZone, setTimeZone, resetTimeZone];
}

var getUnix = function (day, timeZone) {
    switch (timeZone) {
        case TimeZone$1.Local:
            return day.local(true).unix() * MILLISECONDS_PER_SECOND;
        case TimeZone$1.UTC:
            return day.utc(true).unix() * MILLISECONDS_PER_SECOND;
    }
};
var isSameDate = function (a, b) {
    return (a !== null &&
        b !== null &&
        a.year() === b.year() &&
        a.month() === b.month() &&
        a.date() === b.date());
};
var msFromMoment = function (m, timeZone) {
    var newMoment = m.clone();
    switch (timeZone) {
        case TimeZone$1.Local:
            newMoment.local(true);
            break;
        case TimeZone$1.UTC:
            newMoment.utc(true);
            break;
    }
    return newMoment.unix() * MILLISECONDS_PER_SECOND;
};
function useDateTimeRange$1(_a) {
    var absoluteHelpText = _a.absoluteHelpText, controlledFocusedInput = _a.controlledFocusedInput, defaultEndTime = _a.defaultEndTime, defaultStartTime = _a.defaultStartTime, hideClear = _a.hideClear, initialTimeZone = _a.initialTimeZone, max = _a.max, maximumNights = _a.maximumNights, min = _a.min, onChange = _a.onChange, onFocusChange = _a.onFocusChange, onTimeZoneChange = _a.onTimeZoneChange, phrases = _a.phrases, slowPicks = _a.slowPicks, timeFormat = _a.timeFormat, validate = _a.validate, value = _a.value, displayTimeZoneOutside = _a.displayTimeZoneOutside;
    var timeFormat24Hour = timeFormat.replace('hh', 'HH');
    var _b = useAbsoluteRange({
        defaultEndTime: defaultEndTime,
        defaultStartTime: defaultStartTime,
        initialTimeZone: initialTimeZone,
        timeFormat: timeFormat24Hour,
        value: value,
    }), endDate = _b.endDate, endMoment = _b.endMoment, endTime = _b.endTime, handleEndDateChange = _b.handleEndDateChange, handleEndMomentChange = _b.handleEndMomentChange, handleEndTimeChange = _b.handleEndTimeChange, handleStartDateChange = _b.handleStartDateChange, handleStartMomentChange = _b.handleStartMomentChange, handleStartTimeChange = _b.handleStartTimeChange, resetAbsoluteRange = _b.resetAbsoluteRange, setEndDate = _b.setEndDate, setEndMoment = _b.setEndMoment, startDate = _b.startDate, startMoment = _b.startMoment, startTime = _b.startTime;
    var _c = __read(usePhrases(phrases), 3), dateRangePickerPhrases = _c[0], infoPhrases = _c[1], quickPicksPhrases = _c[2];
    var containerRef = React.useRef(null);
    var ref = React.useRef(null);
    var _d = __read(useRelativeRange(value), 3), relativeRange = _d[0], setRelativeRange = _d[1], resetRelativeRange = _d[2];
    var _e = __read(React.useState(null), 2), error = _e[0], setError = _e[1];
    var _f = __read(React.useState(controlledFocusedInput || null), 2), focusedInput = _f[0], setFocusedInput = _f[1];
    var _g = __read(useTimeZone(initialTimeZone), 3), timeZone = _g[0], setTimeZone = _g[1], resetTimeZone = _g[2];
    var _h = __read(useTabs(value), 3), tab = _h[0], setTab = _h[1], resetTab = _h[2];
    var className = React.useMemo(function () {
        var classNames = ['awsui-date-time-range'];
        if (tab === Tab$1.Absolute) {
            classNames.push('awsui-date-time-range--absolute');
        }
        if (tab === Tab$1.Relative) {
            classNames.push('awsui-date-time-range--relative');
        }
        return classNames.join(' ');
    }, [tab]);
    var maxUnix = React.useMemo(function () {
        var maxUnix = typeof max === 'function' ? max(timeZone) : max;
        if (maximumNights && startMoment && focusedInput === 'endDate') {
            maxUnix = Math.min(maxUnix, setTime(startMoment, '23:59:59').unix() * MILLISECONDS_PER_SECOND +
                maximumNights * MILLISECONDS_PER_DAY);
        }
        return maxUnix;
    }, [focusedInput, max, maximumNights, startMoment, timeZone]);
    var isOutsideRange = React.useCallback(function (day) {
        var unix = getUnix(day, timeZone);
        return unix < min || unix > maxUnix;
    }, [maxUnix, min, timeZone]);
    var handleFocusChange = React.useCallback(function (newFocusedInput) {
        if (typeof controlledFocusedInput === 'undefined') {
            setFocusedInput(newFocusedInput);
        }
        if (onFocusChange) {
            if (newFocusedInput !== focusedInput) {
                onFocusChange(newFocusedInput);
            }
        }
    }, [controlledFocusedInput, focusedInput, onFocusChange]);
    var handleApply = React.useCallback(function () {
        batchedUpdates(function () {
            var newValue = relativeRange;
            if (tab === Tab$1.Absolute) {
                var error_1 = null;
                if (startTime !== '' &&
                    endTime !== '' &&
                    startDate === '' &&
                    endDate === '') {
                    error_1 = new Error(InfoErrorMessage$1.START_END_DATE_BLANK);
                }
                else if (startTime !== '' && startDate === '') {
                    error_1 = new Error(InfoErrorMessage$1.START_DATE_BLANK);
                }
                else if (startTime === '' && startDate !== '') {
                    error_1 = new Error(InfoErrorMessage$1.START_TIME_BLANK);
                }
                else if (endTime === '' && endDate !== '') {
                    error_1 = new Error(InfoErrorMessage$1.END_TIME_BLANK);
                }
                else if (startMoment && isOutsideRange(startMoment)) {
                    error_1 = new Error(InfoErrorMessage$1.START_DATE_OUTSIDE_RANGE);
                }
                else if (endMoment && isOutsideRange(endMoment)) {
                    error_1 = new Error(InfoErrorMessage$1.END_DATE_OUTSIDE_RANGE);
                }
                if (error_1) {
                    setError(error_1);
                    return;
                }
                var rangeStart = null;
                var rangeEnd = null;
                if (startMoment) {
                    rangeStart = msFromMoment(startMoment, timeZone);
                }
                if (endMoment) {
                    rangeEnd = msFromMoment(endMoment, timeZone);
                }
                else if (endTime !== '' && startMoment) {
                    var newEndMoment = setTime(startMoment, endTime);
                    setEndMoment(newEndMoment);
                    setEndDate(newEndMoment.format(DATE_FORMAT));
                    rangeEnd = msFromMoment(newEndMoment, timeZone);
                }
                if (rangeStart && rangeEnd && rangeStart >= rangeEnd) {
                    error_1 = new Error(InfoErrorMessage$1.START_NOT_EARLIER_THAN_END);
                    setError(error_1);
                    return;
                }
                var absoluteRange = [
                    rangeStart,
                    rangeEnd,
                ];
                newValue = absoluteRange;
            }
            if (validate) {
                try {
                    validate(newValue);
                }
                catch (err) {
                    setError(err);
                    return;
                }
            }
            onChange(newValue, timeZone);
            if (onTimeZoneChange && initialTimeZone !== timeZone) {
                onTimeZoneChange(timeZone);
            }
            setError(null);
            handleFocusChange(null);
        });
    }, [
        endDate,
        endMoment,
        endTime,
        handleFocusChange,
        initialTimeZone,
        isOutsideRange,
        onChange,
        onTimeZoneChange,
        relativeRange,
        setEndDate,
        setEndMoment,
        startDate,
        startMoment,
        startTime,
        tab,
        timeZone,
        validate,
    ]);
    var handleClose = React.useCallback(function () {
        batchedUpdates(function () {
            resetAbsoluteRange();
            resetRelativeRange();
            resetTab();
            resetTimeZone();
            setError(null);
            handleFocusChange(null);
        });
    }, [
        handleFocusChange,
        resetAbsoluteRange,
        resetRelativeRange,
        resetTab,
        resetTimeZone,
    ]);
    var handleClear = React.useCallback(function () {
        handleClose();
        if (Array.isArray(value)) {
            onChange([null, null], initialTimeZone);
        }
        else {
            onChange(null, initialTimeZone);
        }
    }, [handleClose, initialTimeZone, onChange, value]);
    var handleDatesChange = React.useCallback(function (dates) {
        if (isSameDate(dates.startDate, startMoment) &&
            isSameDate(dates.endDate, endMoment)) {
            handleEndMomentChange(null);
            return;
        }
        if (!isSameDate(dates.startDate, startMoment)) {
            batchedUpdates(function () {
                handleStartMomentChange(dates.startDate);
                handleEndMomentChange(null);
            });
            return;
        }
        batchedUpdates(function () {
            handleStartMomentChange(dates.startDate);
            handleEndMomentChange(dates.endDate);
            if (dates.endDate) {
                handleFocusChange('startDate');
            }
        });
    }, [
        startMoment,
        endMoment,
        handleEndMomentChange,
        handleStartMomentChange,
        handleFocusChange,
    ]);
    var handleErrorMessageDismiss = React.useCallback(function () {
        setError(null);
    }, []);
    var handleExpand = React.useCallback(function () {
        handleFocusChange('startDate');
    }, [handleFocusChange]);
    var handleQuickPick = React.useCallback(function (value) {
        setRelativeRange(value);
        onChange(value, timeZone);
    }, [onChange, setRelativeRange, timeZone]);
    var renderCalendarInfo = React.useCallback(function () {
        return (React.createElement(Info, { absoluteHelpText: absoluteHelpText, endDate: endDate, endTime: endTime, error: error, hideClear: hideClear, onApply: handleApply, onCancel: handleClose, onClear: handleClear, onEndDateChange: handleEndDateChange, onEndTimeChange: handleEndTimeChange, onErrorMessageDismiss: handleErrorMessageDismiss, onRelativeTimeChange: setRelativeRange, onStartDateChange: handleStartDateChange, onStartTimeChange: handleStartTimeChange, onTabChange: setTab, onTimeZoneChange: setTimeZone, phrases: infoPhrases, relativeTime: relativeRange, slowPicks: slowPicks, startDate: startDate, startTime: startTime, tab: tab, timeFormat: timeFormat, timeZone: timeZone, displayTimeZoneOutside: displayTimeZoneOutside }));
    }, [
        absoluteHelpText,
        displayTimeZoneOutside,
        endDate,
        endTime,
        error,
        handleApply,
        handleClear,
        handleClose,
        handleEndDateChange,
        handleEndTimeChange,
        handleErrorMessageDismiss,
        handleStartDateChange,
        handleStartTimeChange,
        hideClear,
        infoPhrases,
        relativeRange,
        setRelativeRange,
        setTab,
        setTimeZone,
        slowPicks,
        startDate,
        startTime,
        tab,
        timeFormat,
        timeZone,
    ]);
    React.useLayoutEffect(function () {
        if (typeof controlledFocusedInput !== 'undefined') {
            setFocusedInput(controlledFocusedInput);
        }
    }, [controlledFocusedInput]);
    return {
        className: className,
        dateRangePickerPhrases: dateRangePickerPhrases,
        startDate: startMoment,
        endDate: endMoment,
        focusedInput: focusedInput,
        handleClose: handleClose,
        handleDatesChange: handleDatesChange,
        handleExpand: handleExpand,
        handleFocusChange: handleFocusChange,
        handleQuickPick: handleQuickPick,
        isOutsideRange: isOutsideRange,
        isQuickPicksSelected: tab === Tab$1.Relative,
        quickPicksPhrases: quickPicksPhrases,
        ref: ref,
        relativeTime: relativeRange,
        renderCalendarInfo: renderCalendarInfo,
        containerRef: containerRef,
    };
}

var DEFAULT_QUICK_PICKS = [
    5 * MILLISECONDS_PER_MINUTE,
    30 * MILLISECONDS_PER_MINUTE,
    1 * MILLISECONDS_PER_HOUR,
    3 * MILLISECONDS_PER_HOUR,
    12 * MILLISECONDS_PER_HOUR,
];
var deprecationWarning = function (oldProp, newProp, value) {
    if (typeof value !== 'undefined') {
        console.warn("@amzn/polaris-date-time-range's `".concat(oldProp, "` prop is deprecated. Please use `").concat(newProp, "` instead."));
    }
};
var getDefaultMax = function () {
    var DEFAULT_MAX = new Date();
    DEFAULT_MAX.setDate(DEFAULT_MAX.getDate() + 1);
    return DEFAULT_MAX.getTime();
};
function DateTimeRange(_a) {
    var absoluteHelpText = _a.absoluteHelpText, defaultEndTime = _a.defaultEndTime, defaultStartTime = _a.defaultStartTime, _b = _a.hideCustom, hideCustom = _b === void 0 ? false : _b, _c = _a.max, max = _c === void 0 ? getDefaultMax() : _c, maximumNights = _a.maximumNights, _d = _a.min, min = _d === void 0 ? 0 : _d, onChange = _a.onChange, phrases = _a.phrases, _e = _a.quickPicks, quickPicks = _e === void 0 ? DEFAULT_QUICK_PICKS : _e, slowPicks = _a.slowPicks, _f = _a.timeFormat, timeFormat = _f === void 0 ? 'hh:mm:ss' : _f, timeZone = _a.timeZone, validate = _a.validate, value = _a.value, _g = _a.initialMonthOffset, initialMonthOffset = _g === void 0 ? -1 : _g, displayTimeZoneOutside = _a.displayTimeZoneOutside, onTimezoneChange = _a.onTimezoneChange, timezone = _a.timezone, _h = _a.displayFormat, displayFormat = _h === void 0 ? 'YYYY-MM-DD (HH:mm:ss)' : _h, _j = _a.endDateId, endDateId = _j === void 0 ? 'date-time-range__end-date' : _j, _k = _a.endDatePlaceholderText, endDatePlaceholderText = _k === void 0 ? 'End date' : _k, controlledFocusedInput = _a.focusedInput, _l = _a.hideClear, hideClear = _l === void 0 ? false : _l, _m = _a.hideKeyboardShortcutsPanel, hideKeyboardShortcutsPanel = _m === void 0 ? true : _m, _o = _a.inputIconPosition, inputIconPosition = _o === void 0 ? 'after' : _o, _p = _a.minimumNights, minimumNights = _p === void 0 ? 0 : _p, onFocusChange = _a.onFocusChange, _q = _a.readOnly, readOnly = _q === void 0 ? true : _q, _r = _a.showDefaultInputIcon, showDefaultInputIcon = _r === void 0 ? true : _r, _s = _a.startDateId, startDateId = _s === void 0 ? 'date-time-range__start-date' : _s, _t = _a.startDatePlaceholderText, startDatePlaceholderText = _t === void 0 ? 'Start date' : _t, props = __rest(_a, ["absoluteHelpText", "defaultEndTime", "defaultStartTime", "hideCustom", "max", "maximumNights", "min", "onChange", "phrases", "quickPicks", "slowPicks", "timeFormat", "timeZone", "validate", "value", "initialMonthOffset", "displayTimeZoneOutside", "onTimezoneChange", "timezone", "displayFormat", "endDateId", "endDatePlaceholderText", "focusedInput", "hideClear", "hideKeyboardShortcutsPanel", "inputIconPosition", "minimumNights", "onFocusChange", "readOnly", "showDefaultInputIcon", "startDateId", "startDatePlaceholderText"]);
    deprecationWarning('timezone', 'timeZone', timezone);
    deprecationWarning('onTimezoneChange', 'onChange', onTimezoneChange);
    if (displayTimeZoneOutside === undefined) {
        console.warn('@amzn/polaris-date-time-range: Deprecation warning polaris-date-time-range layout is changing. The timezone will be displayed outside by default in the future. To use the new layout set the prop displayTimeZoneOutside=true');
    }
    if (displayTimeZoneOutside === false) {
        throw new Error('@amzn/polaris-date-time-range\nDisplaying the timezone inside is being deprecated. To use the new layout set the prop displayTimeZoneOutside=true');
    }
    var initialTimeZone = timeZone || timezone || TimeZone$1.Local;
    var _u = useDateTimeRange$1({
        absoluteHelpText: absoluteHelpText,
        controlledFocusedInput: controlledFocusedInput,
        defaultEndTime: defaultEndTime,
        defaultStartTime: defaultStartTime,
        hideClear: hideClear,
        initialTimeZone: initialTimeZone,
        max: max,
        maximumNights: maximumNights,
        min: min,
        onChange: onChange,
        onFocusChange: onFocusChange,
        onTimeZoneChange: onTimezoneChange,
        phrases: phrases,
        slowPicks: slowPicks,
        timeFormat: timeFormat,
        validate: validate,
        value: value,
        displayTimeZoneOutside: displayTimeZoneOutside,
    }), className = _u.className, dateRangePickerPhrases = _u.dateRangePickerPhrases, endDate = _u.endDate, focusedInput = _u.focusedInput, handleClose = _u.handleClose, handleDatesChange = _u.handleDatesChange, handleExpand = _u.handleExpand, handleFocusChange = _u.handleFocusChange, handleQuickPick = _u.handleQuickPick, isOutsideRange = _u.isOutsideRange, isQuickPicksSelected = _u.isQuickPicksSelected, quickPicksPhrases = _u.quickPicksPhrases, ref = _u.ref, relativeTime = _u.relativeTime, renderCalendarInfo = _u.renderCalendarInfo, startDate = _u.startDate, containerRef = _u.containerRef;
    var _v = __read(usePhrases(phrases), 2), infoPhrases = _v[1];
    var initialVisibleMonth = function () { return moment().add(initialMonthOffset, 'M'); };
    var handleTimeZoneChange = useCallback(function (newTimeZone) {
        if (initialTimeZone !== newTimeZone) {
            var newValue = convertTimeRangeToNewTimeZone(value, initialTimeZone, newTimeZone);
            onChange(newValue, newTimeZone);
        }
    }, [onChange, value, initialTimeZone]);
    return (React.createElement("div", { className: "awsui-date-time-range-container" },
        React.createElement("div", { className: className, ref: containerRef },
            React.createElement(QuickPicks, { onExpand: handleExpand, onPick: handleQuickPick, phrases: quickPicksPhrases, quickPicks: quickPicks, relativeTime: relativeTime, selected: isQuickPicksSelected, hideCustom: hideCustom }),
            React.createElement(DateRangePicker, __assign({ customArrowIcon: React.createElement(Icon, { name: "angle-right", size: "normal", variant: "subtle" }), customInputIcon: React.createElement(Icon, { name: "calendar" }), endDate: endDate, focusedInput: focusedInput, isOutsideRange: isOutsideRange, keepOpenOnDateSelect: true, navNext: React.createElement(Nav, { variant: "next" }), navPrev: React.createElement(Nav, { variant: "prev" }), onClose: handleClose, onDatesChange: handleDatesChange, onFocusChange: handleFocusChange, phrases: dateRangePickerPhrases, ref: ref, renderCalendarInfo: renderCalendarInfo, startDate: startDate, displayFormat: displayFormat, endDateId: endDateId, endDatePlaceholderText: endDatePlaceholderText, hideKeyboardShortcutsPanel: hideKeyboardShortcutsPanel, inputIconPosition: inputIconPosition, minimumNights: minimumNights, readOnly: readOnly, showDefaultInputIcon: showDefaultInputIcon, startDateId: startDateId, startDatePlaceholderText: startDatePlaceholderText, initialVisibleMonth: initialVisibleMonth }, props))),
        displayTimeZoneOutside && (React.createElement(TimeZonePicker$1, { onChange: handleTimeZoneChange, selectedTimeZone: initialTimeZone, phrases: infoPhrases }))));
}
var dateTimeRange = memo(DateTimeRange);

function useDateTimeRange(initialValue, initialTimeZone) {
    if (initialTimeZone === void 0) { initialTimeZone = TimeZone$1.Local; }
    var _a = __read(React.useState(initialValue), 2), value = _a[0], setValue = _a[1];
    var _b = __read(React.useState(initialTimeZone), 2), timeZone = _b[0], setTimeZone = _b[1];
    var handleChange = React.useCallback(function (newValue, newTimeZone) {
        batchedUpdates(function () {
            setValue(newValue);
            setTimeZone(newTimeZone);
        });
    }, []);
    return [value, timeZone, handleChange];
}

export { TimeZone$1 as TimeZone, TimeZone$1 as Timezone, dateTimeRange as default, useDateTimeRange };
